import { DownOutlined, LinkOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import SelectDropdown from '@/components/Select/SelectDropdown';
import type { SelectOptionT } from '@/components/Select/types';
import DefaultToast from '@/components/Toast/DefaultToast';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import authService from '@/services/auth';
import useToastStore from '@/stores/pure/ToastStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { clipboardCopy } from '@/utils/util';

import { Divider, Flex, Space } from '../../../components/base';
import SettingHeader from '../Header';
import MemberItem from './MemberItem';

const MemberSettingBody = () => {
  const color = useColor();

  const { addToast } = useToastStore((state) => ({ addToast: state.addToast }));
  const [inviteCode, setInviteCode] = useState('');
  const {
    currWorkspace,
    fetchWorkspaceMembers,
    currWorkspaceMembers,
    regenerationWorkspaceKey,
    changeMemberRole,
    kickMember,
  } = useWorkspaceStore((state) => ({
    currWorkspace: state.currWorkspace,
    fetchWorkspaceMembers: state.fetchWorkspaceMembers,
    currWorkspaceMembers: state.currWorkspaceMembers,
    regenerationWorkspaceKey: state.regenerationWorkspaceKey,
    changeMemberRole: state.changeMemberRole,
    kickMember: state.kickMember,
  }));
  const { user } = useUserStore((state) => ({ user: state.user }));
  const [linkType, setLinkType] = useState(LINK_TYPES[1]);

  const isAdmin = useMemo(() => {
    let isAdmin = false;
    currWorkspaceMembers.map((member: MemberT) => {
      if (user && member.user_id === user.user_id && member.admin)
        isAdmin = true;
    });

    return isAdmin;
  }, [user, currWorkspaceMembers]);

  useEffect(() => {
    const getInviteCode = async () => {
      if (!currWorkspace) return;
      const data = await authService.getInviteCode(
        currWorkspace.alias,
        Number(linkType.value) === 1,
      );
      setInviteCode(data.invite_code);
    };
    getInviteCode();
  }, [currWorkspace, linkType]);

  const link = currWorkspace
    ? `${window.location.protocol}//${window.location.host}/login?invite=${inviteCode}`
    : '';

  useEffect(() => {
    fetchWorkspaceMembers();
  }, [fetchWorkspaceMembers, currWorkspace]);

  return (
    <React.Fragment>
      <SettingHeader title="Members" />
      <ScrollView style={{ height: `calc(100% - 56px)` }}>
        <Wrapper d="column">
          <Flex j="space-between" style={{ width: '540px' }}>
            <Typo type="body2" weight="sb" color={color.gray700}>
              Invite link
            </Typo>
            <ResetLink
              onClick={async () => {
                const code = await regenerationWorkspaceKey(
                  Number(linkType.value) === 1,
                );
                setInviteCode(code);
              }}
            >
              Reset Link
            </ResetLink>
          </Flex>
          <Space w="500px" h="16px" />
          <Flex g="20px">
            <Flex g="8px">
              <LinkWrapper a="center">{link}</LinkWrapper>
              <SelectDropdown
                value={linkType.value}
                name="setting-member-dropdown"
                style={{
                  height: 42,
                }}
                data={LINK_TYPES}
                mapper={(op) => (
                  <OptionItemStyle>{op.searchName}</OptionItemStyle>
                )}
                customMapper={() => (
                  <SelectedItemStyle a="center" j="space-between">
                    <OptionItemStyle>{linkType.searchName}</OptionItemStyle>
                    <DownOutlined style={{ fontSize: 14 }} />
                  </SelectedItemStyle>
                )}
                onSelect={(key) => {
                  const index = LINK_TYPES.findIndex(
                    (type) => type.value === key,
                  );
                  setLinkType(LINK_TYPES[index]);
                }}
              />
            </Flex>
            <Button
              onClick={() => {
                clipboardCopy(link);
                addToast(
                  <DefaultToast
                    {...{
                      header: 'Invitation link is copied to clipboard',
                      Icon: (
                        <LinkOutlined
                          style={{ fontSize: '16px', color: color.gray700 }}
                        />
                      ),
                    }}
                  />,
                  { position: 'right' },
                );
              }}
              sizeType="l"
            >
              Copy Link
            </Button>
          </Flex>
          <Space h="40px" />
          <Divider direction="h" backgroundColor={color.gray400} />
          <Space h="40px" />
          <Typo type="body2" weight="sb" color={color.gray700}>
            Members
          </Typo>
          <Space h="16px" />
          <Divider direction="h" backgroundColor={color.gray400} />
          <Flex j="space-between" d="row" style={{ padding: '12px 0' }}>
            <Typo type="body3" weight="sb" color={color.gray800}>
              Name
            </Typo>
            <Typo
              type="body3"
              weight="sb"
              color={color.gray800}
              style={{ width: 400 }}
            >
              Permission
            </Typo>
          </Flex>
          <Divider direction="h" backgroundColor={color.gray400} />
          <ScrollView style={{ height: 'calc(100% - 238px)' }}>
            {currWorkspaceMembers.map((member: MemberT) => (
              <MemberItem
                key={member.email}
                isAdmin={isAdmin}
                changeMemberRole={changeMemberRole}
                kickMember={kickMember}
                {...member}
              ></MemberItem>
            ))}
          </ScrollView>
        </Wrapper>
      </ScrollView>
    </React.Fragment>
  );
};

export default MemberSettingBody;

const Wrapper = styled(Flex)`
  width: 100%;
  padding: 32px;
  overflow: hidden;
  height: 100%;
`;

const LinkWrapper = styled(Flex)`
  width: 540px;
  height: 42px;
  padding: 0px 16px;
  border: 1px solid ${(props) => props.theme.colors.gray500};
  border-radius: 4px;

  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.gray800};
  background-color: ${(props) => props.theme.colors.gray200};
`;

const ResetLink = styled.span`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  text-decoration-line: underline;
  color: ${(props) => props.theme.colors.gray700};
  cursor: pointer;
`;

const OptionItemStyle = styled(Typo)`
  padding: 6px 8px;
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.gray700};
`;

const SelectedItemStyle = styled(Flex)`
  height: 42px;
  padding: 0 8px;
  width: 120px;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.gray700};
`;

const LINK_TYPES: SelectOptionT[] = [
  {
    value: '1',
    searchName: 'as a viewer',
  },
  {
    value: '2',
    searchName: 'as an editor',
  },
];
